import dayjs from 'dayjs'
import * as XLSX from 'xlsx'

import { axiosInstance } from '../../../../hooks/useAxios'

import type { IFilterMapped } from './components/CustomerReportFilter'

/** ดึงต่อรอบตอน export — ไม่เกี่ยวกับ perPage บนตาราง (ลดจำนวน request) */
export const CUSTOMER_REPORT_EXPORT_PAGE_SIZE = 200

type CustomerReportItem = {
  date?: string | Date
  productName?: string
  quantity?: number
  total?: number
}

type CustomerReportRecord = {
  name?: string
  phone?: string
  items?: CustomerReportItem[]
  order?: number
  customerCode?: string
  phone2?: string
  email?: string
  gender?: string
  customerGroupName?: string
  address?: string
  subdistrict?: string
  district?: string
  province?: string
  postalCode?: string
  country?: string
  lineId?: string
  score?: number
  tags?: string[]
  createdAt?: string | Date
  customerSince?: string | Date
}

function formatItemDateBe(value: unknown): string {
  if (value == null || value === '') return ''
  const d = dayjs(value as string | Date)
  if (!d.isValid()) return ''
  return d.format('D/M/BBBB')
}

export function buildCustomerReportExcelRows(
  records: CustomerReportRecord[],
): (string | number)[][] {
  const rows: (string | number)[][] = []

  for (const row of records) {
    const name = row.name ?? ''
    const phone = row.phone ?? ''
    rows.push([name, '', phone, ''])
    rows.push(['วันที่', 'สินค้า', 'ราคา', 'จำนวน'])

    const items = Array.isArray(row.items) ? row.items : []
    let sumQty = 0
    let sumTotal = 0

    for (const item of items) {
      const qty = Number(item.quantity ?? 0)
      const lineTotal = Number(item.total ?? 0)
      sumQty += qty
      sumTotal += lineTotal
      rows.push([
        formatItemDateBe(item.date),
        item.productName ?? '',
        lineTotal,
        qty,
      ])
    }

    rows.push(['', '', sumTotal, sumQty])
    rows.push([])
  }

  return rows
}

export function downloadCustomerReportExcel(
  records: CustomerReportRecord[],
  filter: Pick<IFilterMapped, 'startDate' | 'endDate'>,
): void {
  const rows = buildCustomerReportExcelRows(records)
  const ws = XLSX.utils.aoa_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'รายงานลูกค้า')

  const from = filter.startDate
    ? dayjs(filter.startDate).format('YYYYMMDD')
    : 'start'
  const to = filter.endDate ? dayjs(filter.endDate).format('YYYYMMDD') : 'end'
  XLSX.writeFile(wb, `customer-report-${from}-${to}.xlsx`)
}

function getNestedValue(
  source: unknown,
  path: string,
): unknown {
  if (source == null || typeof source !== 'object') return undefined
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined
    return (acc as Record<string, unknown>)[key]
  }, source)
}

function pickValue(
  record: CustomerReportRecord,
  paths: string[],
): unknown {
  for (const path of paths) {
    const value = getNestedValue(record, path)
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }
  return undefined
}

function valueToString(value: unknown): string {
  if (value == null) return ''
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item ?? '').trim())
      .filter(Boolean)
      .join(', ')
  }
  return String(value)
}

function formatDateTime(value: unknown): string {
  if (value == null || value === '') return ''
  const d = dayjs(value as string | Date)
  if (!d.isValid()) return ''
  return d.format('YYYY-MM-DD HH:mm:ss')
}

export function buildCustomerListExcelRows(
  records: CustomerReportRecord[],
  filter: Pick<IFilterMapped, 'startDate' | 'endDate'>,
): (string | number)[][] {
  const fromText = filter.startDate
    ? dayjs(filter.startDate).format('YYYY-MM-DD')
    : '-'
  const toText = filter.endDate ? dayjs(filter.endDate).format('YYYY-MM-DD') : '-'

  const rows: (string | number)[][] = [
    ['', 'ส่งออกข้อมูลลูกค้า (Export Customers)'],
    ['ระยะเวลาของข้อมูล : ', `${fromText} ถึง ${toText}`, 'จำนวนข้อมูล : ', records.length],
    [
      // 'รหัสลูกค้า',
      'ลูกค้า',
      'เบอร์โทร1',
      'อีเมล',
      'ที่อยู่',
      'แขวง/ ตำบล',
      'เขต/ อำเภอ',
      'จังหวัด',
      'รหัสไปรษณีย์',
      'ประเทศ',
      'ไอดีไลน์/facebook',
      'วันที่คำสั่งซื้อแรก',
      'จำนวนครั้งที่สั่งซื้อ',
    ],
  ]

  for (const record of records) {
    const phoneValue = pickValue(record, ['phone', 'customer.phone', 'user.phone'])
    const orderCount = Number(
      pickValue(record, ['order', 'orderCount', 'totalOrder', 'customer.order']) ?? 0,
    )
    rows.push([
      // valueToString(pickValue(record, ['customerCode', 'code', 'customer.code'])),
      valueToString(pickValue(record, ['name', 'customer.name', 'user.name'])),
      valueToString(phoneValue),
      valueToString(pickValue(record, ['email', 'customer.email', 'user.email'])),
      valueToString(pickValue(record, ['address', 'customer.address', 'user.address'])),
      valueToString(
        pickValue(record, [
          'subdistrict',
          'subDistrict',
          'addressSubdistrict',
          'customer.subdistrict',
          'customer.subDistrict',
        ]),
      ),
      valueToString(
        pickValue(record, [
          'district',
          'addressDistrict',
          'customer.district',
        ]),
      ),
      valueToString(
        pickValue(record, ['province', 'addressProvince', 'customer.province']),
      ),
      valueToString(
        pickValue(record, ['postalCode', 'zipCode', 'customer.postalCode']),
      ),
      valueToString(pickValue(record, ['country', 'customer.country'])),
      valueToString(pickValue(record, ['lineId', 'line', 'customer.lineId'])),
      formatDateTime(
        pickValue(record, [
          'customerSince',
          'createdAt',
          'customer.createdAt',
          'customerSinceAt',
        ]),
      ),
      Number.isFinite(orderCount) ? orderCount : 0,
    ])
  }

  return rows
}

export function downloadCustomerListExcel(
  records: CustomerReportRecord[],
  filter: Pick<IFilterMapped, 'startDate' | 'endDate'>,
): void {
  const rows = buildCustomerListExcelRows(records, filter)
  const ws = XLSX.utils.aoa_to_sheet(rows)
  ws['!merges'] = [{ s: { r: 0, c: 1 }, e: { r: 0, c: 3 } }]
  ws['!cols'] = [
    { wch: 14 },
    { wch: 24 },
    { wch: 14 },
    { wch: 24 },
    { wch: 42 },
    { wch: 16 },
    { wch: 16 },
    { wch: 14 },
    { wch: 12 },
    { wch: 10 },
    { wch: 14 },
    { wch: 20 },
    { wch: 16 },
  ]
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'รายชื่อลูกค้า')

  const from = filter.startDate
    ? dayjs(filter.startDate).format('YYYYMMDD')
    : 'start'
  const to = filter.endDate ? dayjs(filter.endDate).format('YYYYMMDD') : 'end'
  XLSX.writeFile(wb, `customer-list-${from}-${to}.xlsx`)
}

/**
 * พารามิเตอร์รายงานลูกค้า — ตัด `page` / `perPage` ของตารางออก เหลือเฉพาะเงื่อนไขค้นหา + page/pageSize ของรอบนี้
 */
function buildCustomerReportListParams(
  appliedFilter: IFilterMapped,
  page: number,
  pageSize: number,
): Record<string, string | number> {
  const p: Record<string, string | number> = {
    startDate: appliedFilter.startDate ?? '',
    endDate: appliedFilter.endDate ?? '',
    type: 'order',
    page,
    pageSize,
  }
  if (appliedFilter.search != null && appliedFilter.search !== '') {
    p.search = appliedFilter.search
  }
  if (appliedFilter.minTotal != null) {
    p.minTotal = appliedFilter.minTotal
  }
  if (appliedFilter.maxTotal != null) {
    p.maxTotal = appliedFilter.maxTotal
  }
  if (appliedFilter.sortBy != null && appliedFilter.sortBy !== '') {
    p.sortBy = appliedFilter.sortBy
  }
  if (appliedFilter.productId != null && appliedFilter.productId !== '') {
    p.productId = appliedFilter.productId
  }
  if (appliedFilter.categoryId != null && appliedFilter.categoryId !== '') {
    p.categoryId = appliedFilter.categoryId
  }
  return p
}

export type CustomerReportFetchProgress = {
  page: number
  totalPages: number
  /** จำนวนลูกค้าทั้งหมดจาก API (หลังรู้ count จากหน้าแรก) */
  totalCustomers?: number
}

/** ผู้ใช้กดยกเลิก / AbortController ระหว่างดึงข้อมูล export */
export function isExportFetchCanceled(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const e = error as { code?: string; name?: string }
  return e.code === 'ERR_CANCELED' || e.name === 'CanceledError'
}

export async function fetchAllCustomerReportRecords(
  /** filter หลังกดค้นหาแล้ว (searchFilter) — ไม่ใช่ค่าที่ยังแก้ในฟอร์มอย่างเดียว */
  appliedFilter: IFilterMapped,
  onProgress?: (info: CustomerReportFetchProgress) => void,
  options?: { pageSize?: number; signal?: AbortSignal },
): Promise<CustomerReportRecord[]> {
  const pageSize = Math.max(
    1,
    options?.pageSize ?? CUSTOMER_REPORT_EXPORT_PAGE_SIZE,
  )
  const { signal } = options ?? {}

  const paramsForPage = (page: number) =>
    buildCustomerReportListParams(appliedFilter, page, pageSize)

  const firstRes = await axiosInstance.get('/report/customer-daily-reports', {
    params: paramsForPage(1),
    signal,
  })

  const body = firstRes.data as
    | CustomerReportRecord[]
    | {
        records?: CustomerReportRecord[]
        count?: number
      }

  const firstRecords: CustomerReportRecord[] = Array.isArray(body)
    ? body
    : body?.records ?? []

  const countField =
    !Array.isArray(body) && typeof body?.count === 'number'
      ? Number(body.count)
      : undefined

  if (countField === undefined) {
    onProgress?.({ page: 1, totalPages: 1 })
    return firstRecords
  }

  const count = countField
  const totalPages = Math.max(1, Math.ceil(count / pageSize))
  onProgress?.({ page: 1, totalPages, totalCustomers: count })

  const all: CustomerReportRecord[] = [...firstRecords]

  for (let p = 2; p <= totalPages; p++) {
    onProgress?.({ page: p, totalPages, totalCustomers: count })
    const res = await axiosInstance.get('/report/customer-daily-reports', {
      params: paramsForPage(p),
      signal,
    })
    const pageBody = res.data as
      | CustomerReportRecord[]
      | { records?: CustomerReportRecord[] }
    const chunk: CustomerReportRecord[] = Array.isArray(pageBody)
      ? pageBody
      : pageBody?.records ?? []
    all.push(...chunk)
  }

  return all
}
